// =============================================================================
// MeuDriver — Batch R4: AdminSupport
// Lista de todos os tickets de suporte + filtros + resposta + status
// Rota: /admin/suporte
// =============================================================================

import { useState } from 'react'
import {
  LifeBuoy,
  Loader2,
  ChevronRight,
  Send,
  MessageCircle,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Filter,
} from 'lucide-react'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toast } from 'sonner'
import {
  useAdminTickets,
  useTicketMessages,
  useSendTicketMessage,
  useUpdateTicketStatus,
} from '@/hooks/useSupportTickets'
import type { SupportTicketRow, SupportTicketStatus, SupportTicketPriority } from '@/types/database'
import { useAuth } from '@/hooks/useAuth'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: SupportTicketStatus }) {
  const map: Record<SupportTicketStatus, { label: string; className: string }> = {
    open:        { label: 'Aberto',     className: 'bg-blue-100 text-blue-700' },
    in_progress: { label: 'Em análise', className: 'bg-yellow-100 text-yellow-700' },
    resolved:    { label: 'Resolvido',  className: 'bg-green-100 text-green-700' },
    closed:      { label: 'Fechado',    className: 'bg-gray-100 text-gray-500' },
  }
  const { label, className } = map[status]
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${className}`}>
      {label}
    </span>
  )
}

function PriorityBadge({ priority }: { priority: SupportTicketPriority }) {
  const map: Record<SupportTicketPriority, { label: string; className: string }> = {
    low:    { label: 'Baixa',  className: 'bg-slate-100 text-slate-500' },
    medium: { label: 'Média',  className: 'bg-orange-100 text-orange-600' },
    high:   { label: 'Alta',   className: 'bg-red-100 text-red-600' },
  }
  const cfg = map[priority] ?? map.medium
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${cfg.className}`}>
      {cfg.label}
    </span>
  )
}

function StatusIcon({ status }: { status: SupportTicketStatus }) {
  switch (status) {
    case 'open':        return <Clock        className="h-4 w-4 text-blue-500" />
    case 'in_progress': return <AlertCircle  className="h-4 w-4 text-yellow-500" />
    case 'resolved':    return <CheckCircle2 className="h-4 w-4 text-green-500" />
    case 'closed':      return <XCircle      className="h-4 w-4 text-gray-400" />
  }
}

// ---------------------------------------------------------------------------
// Detalhe do ticket (admin)
// ---------------------------------------------------------------------------

interface AdminTicketDetailProps {
  ticket: SupportTicketRow
  onClose: () => void
}

function AdminTicketDetail({ ticket, onClose }: AdminTicketDetailProps) {
  const { user } = useAuth()
  const { data: messages = [], isLoading } = useTicketMessages(ticket.id)
  const send = useSendTicketMessage()
  const updateStatus = useUpdateTicketStatus()
  const [body, setBody] = useState('')
  const [localStatus, setLocalStatus] = useState<SupportTicketStatus>(ticket.status)
  const [localPriority, setLocalPriority] = useState<SupportTicketPriority>(ticket.priority)

  async function handleSend() {
    if (!body.trim()) return
    try {
      await send.mutateAsync({ ticketId: ticket.id, body: body.trim() })
      setBody('')
    } catch {
      toast.error('Erro ao enviar resposta.')
    }
  }

  async function handleSaveChanges() {
    try {
      await updateStatus.mutateAsync({
        id:       ticket.id,
        status:   localStatus,
        priority: localPriority,
      })
      toast.success('Ticket atualizado.')
    } catch {
      toast.error('Erro ao atualizar ticket.')
    }
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-start gap-2 pr-6">
            <DialogTitle className="text-base leading-snug flex-1">{ticket.subject}</DialogTitle>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <StatusBadge status={ticket.status} />
            <PriorityBadge priority={ticket.priority} />
            <span className="text-xs text-muted-foreground">
              #{ticket.id.slice(0, 8)} ·{' '}
              {formatDistanceToNow(parseISO(ticket.created_at), { addSuffix: true, locale: ptBR })}
            </span>
          </div>
        </DialogHeader>

        <Separator />

        {/* Descrição */}
        <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-700 shrink-0">
          <p className="font-medium text-xs text-muted-foreground mb-1">Descrição:</p>
          <p className="whitespace-pre-line">{ticket.description}</p>
        </div>

        {/* Controles de status/prioridade */}
        <div className="flex flex-wrap gap-3 shrink-0">
          <div className="flex-1 min-w-[140px] space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Status</p>
            <Select
              value={localStatus}
              onValueChange={(v) => setLocalStatus(v as SupportTicketStatus)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Aberto</SelectItem>
                <SelectItem value="in_progress">Em análise</SelectItem>
                <SelectItem value="resolved">Resolvido</SelectItem>
                <SelectItem value="closed">Fechado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 min-w-[140px] space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Prioridade</p>
            <Select
              value={localPriority}
              onValueChange={(v) => setLocalPriority(v as SupportTicketPriority)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Baixa</SelectItem>
                <SelectItem value="medium">Média</SelectItem>
                <SelectItem value="high">Alta</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="self-end">
            <Button
              size="sm"
              variant="outline"
              onClick={handleSaveChanges}
              disabled={
                updateStatus.isPending ||
                (localStatus === ticket.status && localPriority === ticket.priority)
              }
              className="h-8 text-xs"
              data-testid="save-ticket-changes-btn"
            >
              {updateStatus.isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Salvar
            </Button>
          </div>
        </div>

        <Separator />

        {/* Thread */}
        <ScrollArea className="flex-1 min-h-0 max-h-52">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center py-8 gap-2 text-center text-muted-foreground">
              <MessageCircle className="h-8 w-8 opacity-20" />
              <p className="text-sm">Nenhuma mensagem ainda.</p>
            </div>
          ) : (
            <div className="space-y-3 py-2 px-1">
              {messages.map((m) => {
                const isStaff = m.is_staff_reply || m.sender_id === user?.id
                return (
                  <div key={m.id} className={`flex ${isStaff ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                        isStaff
                          ? 'bg-orange-500 text-white rounded-br-md'
                          : 'bg-gray-100 text-gray-800 rounded-bl-md'
                      }`}
                    >
                      {isStaff && (
                        <p className="text-[10px] font-bold text-white/80 mb-0.5 uppercase tracking-wide">
                          Suporte
                        </p>
                      )}
                      <p className="whitespace-pre-line leading-snug">{m.body}</p>
                      <p className={`text-[10px] mt-1 ${isStaff ? 'text-white/60' : 'text-muted-foreground'}`}>
                        {formatDistanceToNow(parseISO(m.created_at), { addSuffix: true, locale: ptBR })}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </ScrollArea>

        {/* Resposta do staff */}
        <Separator />
        <div className="flex gap-2 shrink-0">
          <Textarea
            placeholder="Responder ao usuário... (Ctrl+Enter para enviar)"
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={2000}
            className="resize-none flex-1"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend()
            }}
            data-testid="admin-reply-input"
          />
          <Button
            onClick={handleSend}
            disabled={!body.trim() || send.isPending}
            size="icon"
            className="h-full px-3 self-end bg-orange-500 hover:bg-orange-600"
            data-testid="admin-send-reply-btn"
          >
            {send.isPending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Send className="h-4 w-4" />
            }
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Página principal (Admin)
// ---------------------------------------------------------------------------

const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all',         label: 'Todos' },
  { value: 'open',        label: 'Abertos' },
  { value: 'in_progress', label: 'Em análise' },
  { value: 'resolved',    label: 'Resolvidos' },
  { value: 'closed',      label: 'Fechados' },
]

export default function AdminSupport() {
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedTicket, setSelectedTicket] = useState<SupportTicketRow | null>(null)

  const { data: tickets = [], isLoading } = useAdminTickets(
    statusFilter !== 'all' ? (statusFilter as SupportTicketStatus) : undefined,
  )

  return (
    <div className="max-w-4xl mx-auto space-y-6">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <LifeBuoy className="h-6 w-6 text-orange-500" />
          Suporte — Tickets
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Gerencie os tickets de suporte de alunos e instrutores.
        </p>
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                statusFilter === opt.value
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`filter-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-3 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
              <LifeBuoy className="h-7 w-7 text-gray-300" />
            </div>
            <p className="font-medium text-gray-700">Nenhum ticket encontrado</p>
            <p className="text-sm text-muted-foreground">
              {statusFilter !== 'all'
                ? 'Tente outro filtro de status.'
                : 'Nenhum ticket aberto ainda.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <button
              key={t.id}
              data-testid="admin-ticket-row"
              className="w-full text-left rounded-xl border border-gray-200 bg-white p-4 hover:border-orange-300 hover:bg-orange-50/30 transition-all"
              onClick={() => setSelectedTicket(t)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="mt-0.5 shrink-0">
                    <StatusIcon status={t.status} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-gray-900 truncate">{t.subject}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                      {t.description}
                    </p>
                    <p className="text-[11px] text-muted-foreground/70 mt-1">
                      #{t.id.slice(0, 8)} ·{' '}
                      {formatDistanceToNow(parseISO(t.updated_at), { addSuffix: true, locale: ptBR })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <PriorityBadge priority={t.priority} />
                  <StatusBadge status={t.status} />
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Detalhe */}
      {selectedTicket && (
        <AdminTicketDetail
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
        />
      )}
    </div>
  )
}
